// bluaIQ — P1: Change Orders Module
// Track change orders with approval workflow and cost/schedule impact

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, DollarSign, Clock, AlertTriangle, ChevronDown, ChevronUp, Download, Info } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { downloadCsv } from '../utils/exportCsv'
import { formatMoneyValue, formatMoney } from '../utils/money'
// D-25AGO-09 / spec R-1.1.a: ONE exported union is the source of truth for the
// permitted statuses, imported by the zod schema, the server guard AND this
// badge map. A client-side status list that is not this import is PROHIBITED.
import { isValidChangeOrderTransition, type ChangeOrderStatus } from '../services/changeOrderStateMachine'

// ── TYPES ─────────────────────────────────────────

interface ChangeOrder {
  id: string
  title: string
  description: string
  reason: string
  amount: number
  phase: string
  impactDays: number
  status: string
  requestedBy: string
  createdAt: string
  approvedAt?: string
}

const REASON_KEYS = ['CLIENT', 'DESIGN', 'SITE', 'CODE', 'VALUE'] as const

// D-25AGO-09 / spec R-1.1.d: a state the SERVER CAN NEVER PERSIST must not be
// rendered as a badge. `DRAFT` used to sit in this map (and doubled as the
// fallback style), yet `ChangeOrderSchema` carries no `status` key, so a
// client-sent DRAFT is stripped by zod and the row lands on the DB default
// PENDING. The badge was showing a state the database cannot hold. The map is
// now keyed by the SHARED union, and the fallback is a neutral "unknown" style
// that does not impersonate a real state.
const STATUS_STYLES: Record<ChangeOrderStatus, { bg: string; text: string }> = {
  PENDING:  { bg: '#EEF2FF', text: '#06539B' },
  APPROVED: { bg: '#ECFDF5', text: '#22C55E' },
  REJECTED: { bg: '#FEF2F2', text: '#EF4444' },
}
const UNKNOWN_STATUS_STYLE = { bg: '#F2F0EB', text: '#9E9890' }

// D-25AGO-09 — approve / reject copy. Code-keyed EN/ES: no raw server string
// ever reaches the user, and both languages ship together so neither can drift.
const CO_DECISION_COPY = {
  approveBtn: { en: 'Approve', es: 'Aprobar' },
  rejectBtn: { en: 'Reject', es: 'Rechazar' },
  approveTitle: { en: 'Approve this change order', es: 'Aprobar esta orden de cambio' },
  rejectTitle: { en: 'Reject this change order', es: 'Rechazar esta orden de cambio' },
  // Spec §2.3 heading 3: a one-click approve with no amount shown is PROHIBITED.
  // The dialog states the amount AND the resulting approved total.
  approveBody: {
    en: 'Approving records this amount as an agreed change to the contract. It cannot be undone — a correction is a new change order.',
    es: 'Aprobar registra este monto como un cambio acordado al contrato. No se puede deshacer — una corrección es una nueva orden de cambio.',
  },
  rejectBody: {
    en: 'Rejecting is final. A rejected change order is not reopened — it is replaced by a new one.',
    es: 'Rechazar es definitivo. Una orden de cambio rechazada no se reabre — se reemplaza por una nueva.',
  },
  amountLabel: { en: 'Amount', es: 'Monto' },
  resultingTotalLabel: { en: 'Approved change orders after this', es: 'Órdenes de cambio aprobadas después de esto' },
  reasonLabel: { en: 'Reason (required)', es: 'Motivo (requerido)' },
  reasonPlaceholder: { en: 'Why is this being rejected?', es: '¿Por qué se rechaza?' },
  confirmApprove: { en: 'Confirm approval', es: 'Confirmar aprobación' },
  confirmReject: { en: 'Confirm rejection', es: 'Confirmar rechazo' },
  keepCta: { en: 'Cancel', es: 'Cancelar' },
  workingBtn: { en: 'Saving…', es: 'Guardando…' },
  reasonRequired: { en: 'A reason is required to reject.', es: 'Se requiere un motivo para rechazar.' },
  errConflict: {
    en: 'This change order was already decided. Reload to see its current state.',
    es: 'Esta orden de cambio ya fue decidida. Recarga para ver su estado actual.',
  },
  errGeneric: {
    en: 'Could not update this change order. Please try again.',
    es: 'No se pudo actualizar esta orden de cambio. Inténtalo de nuevo.',
  },
  // The existing trust note says COs do not update Budget or Gantt. Approving
  // does not change that, and saying so here keeps the promise honest now that
  // a decision verb exists.
  downstreamNote: {
    en: 'Approving does not update the Budget or the Gantt schedule.',
    es: 'Aprobar no actualiza el Presupuesto ni el Gantt.',
  },
} as const

// Map a transition error response to localized copy — never the raw server string.
const localizeDecisionError = (status: number, isEs: boolean): string => {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  if (status === 409) return L(CO_DECISION_COPY.errConflict)
  return L(CO_DECISION_COPY.errGeneric)
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_CHANGE_ORDERS: ChangeOrder[] = [
  {
    id: 'co-1', title: 'Upgrade to quartz countertops', description: 'Client requested quartz instead of granite for kitchen and bathrooms',
    reason: 'CLIENT', amount: 4200, phase: 'Finishes', impactDays: 3, status: 'APPROVED',
    requestedBy: 'Maria Lopez', createdAt: '2026-03-10', approvedAt: '2026-03-12',
  },
  {
    id: 'co-2', title: 'Add recessed lighting in living room', description: 'Design change to add 8 recessed LED fixtures in main living area',
    reason: 'DESIGN', amount: 1800, phase: 'Electrical', impactDays: 1, status: 'APPROVED',
    requestedBy: 'Carlos Mendez', createdAt: '2026-03-15', approvedAt: '2026-03-17',
  },
  {
    id: 'co-3', title: 'Foundation soil remediation', description: 'Expansive clay found during excavation requires lime stabilization',
    reason: 'SITE', amount: 3500, phase: 'Foundation', impactDays: 5, status: 'PENDING',
    requestedBy: 'Solid Ground LLC', createdAt: '2026-03-22',
  },
  {
    id: 'co-4', title: 'Tankless water heater upgrade', description: 'Switch from tank to tankless water heater for energy efficiency',
    reason: 'VALUE', amount: -600, phase: 'Plumbing', impactDays: 0, status: 'PENDING',
    requestedBy: 'Torres Plumbing', createdAt: '2026-03-25',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function ChangeOrderModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [showForm, setShowForm] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  // D-25AGO-09 approve/reject. One target at a time: an open confirmation is a
  // deliberate, single decision, never a row of one-click buttons.
  const [decision, setDecision] = useState<{ id: string; next: ChangeOrderStatus } | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)
  // Demo projects have no rows in the database, so a write would 404. The
  // decision surface is hidden there rather than offered and then refused.
  const isDemo = isDemoProject(projectId)

  const openDecision = (id: string, next: ChangeOrderStatus) => {
    setDecision({ id, next })
    setRejectReason('')
    setDecisionError(null)
  }
  const closeDecision = () => {
    setDecision(null)
    setRejectReason('')
    setDecisionError(null)
  }

  const runDecision = async (order: ChangeOrder, next: ChangeOrderStatus) => {
    if (next === 'REJECTED' && rejectReason.trim().length === 0) {
      setDecisionError(isEs ? CO_DECISION_COPY.reasonRequired.es : CO_DECISION_COPY.reasonRequired.en)
      return
    }
    setDeciding(true)
    setDecisionError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/change-orders/${order.id}`, {
        method: 'PATCH',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: next,
          ...(next === 'REJECTED' ? { rejectionReason: rejectReason.trim() } : {}),
        }),
      })
      if (!res.ok) {
        setDecisionError(localizeDecisionError(res.status, isEs))
        return
      }
      closeDecision()
      queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] })
    } catch {
      setDecisionError(isEs ? CO_DECISION_COPY.errGeneric.es : CO_DECISION_COPY.errGeneric.en)
    } finally {
      setDeciding(false)
    }
  }

  const { data: apiData, isLoading } = useQuery<ChangeOrder[]>({
    queryKey: ['change-orders', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/change-orders`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const orders = Array.isArray(apiData) ? apiData : (isDemoProject(projectId) ? DEMO_CHANGE_ORDERS : [])

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>

  const approved = orders.filter(o => o.status === 'APPROVED')
  const pending = orders.filter(o => o.status === 'PENDING')
  const approvedTotal = approved.reduce((s, o) => s + o.amount, 0)
  const pendingTotal = pending.reduce((s, o) => s + o.amount, 0)
  const totalImpactDays = orders.filter(o => o.status !== 'REJECTED').reduce((s, o) => s + o.impactDays, 0)

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' }) : '—'

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {t('changeOrders.title')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {orders.length} {t('changeOrders.ordersLabel')} · {approved.length} {t('changeOrders.approvedCountLabel')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {orders.length > 0 && (
            <button
              onClick={() => downloadCsv({
                filename: `change-orders-${projectId}`,
                headers: [t('changeOrders.csv.title'), t('changeOrders.csv.reason'), t('changeOrders.csv.phase'), t('changeOrders.csv.amount'), t('changeOrders.csv.impactDays'), t('changeOrders.csv.status'), t('changeOrders.csv.requestedBy'), t('changeOrders.csv.date')],
                rows: orders.map(o => [o.title, o.reason, o.phase, o.amount, o.impactDays, o.status, o.requestedBy, o.createdAt])
              })}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Download size={14} />
              CSV
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
          >
            <Plus size={14} />
            {t('changeOrders.newOrder')}
          </button>
        </div>
      </div>

      {/* PR-F4: source-honesty trust note — COs are separate from Budget/Gantt */}
      <div data-testid="co-trust-note" className="flex items-start gap-2 text-[10px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2 leading-relaxed">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          {isEs
            ? 'Las órdenes de cambio se registran por separado. Aún no actualizan el presupuesto ni el Gantt. Las aprobadas pueden usarse en AIA Billing — revisa la alineación de fuentes.'
            : 'Change orders are tracked separately. They do not update the Budget or Gantt schedule yet. Approved change orders may be used in AIA Billing — verify source alignment.'}
        </span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('changeOrders.kpi.approved')}</div>
          <div className="text-[18px] font-[900] text-[#22C55E]">{formatMoneyValue(approvedTotal, isEs)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{approved.length} {t('changeOrders.ordersLabel')}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('changeOrders.kpi.pending')}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{formatMoneyValue(pendingTotal, isEs)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{pending.length} {t('changeOrders.ordersLabel')}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('changeOrders.kpi.scheduleImpact')}</div>
          <div className="text-[18px] font-[900] text-[#1C1A17]">{totalImpactDays} {t('changeOrders.kpi.days')}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{t('changeOrders.kpi.cumulativeTotal')}</div>
          {/* PR-F4: days total is manual tracking only — not wired to Gantt */}
          <div data-testid="co-impact-days-note" className="text-[8px] font-[600] text-[#9E9890] mt-0.5">
            {isEs ? 'No actualiza el Gantt automáticamente.' : 'Does not update Gantt automatically.'}
          </div>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <AddChangeOrderForm
          projectId={projectId!}
          onClose={() => setShowForm(false)}
          onAdded={() => { setShowForm(false); queryClient.invalidateQueries({ queryKey: ['change-orders', projectId] }) }}
        />
      )}

      {/* Change order list */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#ECEAE4]">
          <h3 className="text-[13px] font-[800]">{t('changeOrders.allOrders')}</h3>
        </div>

        {orders.length === 0 && (
          <div className="text-center py-12">
            <DollarSign size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            <p className="text-[12px] font-[700] text-[#9E9890]">
              {t('changeOrders.noOrders')}
            </p>
          </div>
        )}

        {orders.map(order => {
          const style = STATUS_STYLES[order.status as ChangeOrderStatus] || UNKNOWN_STATUS_STYLE
          const isExpanded = expanded === order.id
          const isDecisionTarget = decision?.id === order.id
          const isApproving = decision?.next === 'APPROVED'

          return (
            <div key={order.id} className="border-t border-[#ECEAE4]">
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                onClick={() => setExpanded(isExpanded ? null : order.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-[800] text-[#1C1A17]">{order.title}</span>
                    <span
                      className="text-[8px] font-[800] px-1.5 py-0.5 rounded-full"
                      style={{ background: style.bg, color: style.text }}
                    >
                      {t(`changeOrders.status.${order.status}`)}
                    </span>
                  </div>
                  <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                    {t(`changeOrders.reason.${order.reason}`)} · {order.phase} · {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {(() => {
                    // PR-F4: safe amount — allowNegative credits are valid COs.
                    const amt = formatMoney(order.amount, { isEs, allowNegative: true })
                    return (
                      <div className={`text-[13px] font-[900] ${amt.isValid ? (order.amount >= 0 ? 'text-[#1C1A17]' : 'text-[#22C55E]') : 'text-[#9E9890]'}`}>
                        {amt.isValid && order.amount > 0 ? '+' : ''}{amt.displayValue}
                      </div>
                    )
                  })()}
                  {order.impactDays > 0 && (
                    <div className="text-[9px] font-[700] text-[#9E9890]">+{order.impactDays} {t('changeOrders.daysShort')}</div>
                  )}
                </div>
                {isExpanded ? <ChevronUp size={14} className="text-[#9E9890]" /> : <ChevronDown size={14} className="text-[#9E9890]" />}
              </div>

              {isExpanded && (
                <div className="px-4 pb-3 pl-8 space-y-2">
                  <p className="text-[11px] font-[600] text-[#9E9890]">{order.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-[600] text-[#9E9890]">
                    <span>{t('changeOrders.requestedByLabel')}: <strong className="text-[#1C1A17]">{order.requestedBy}</strong></span>
                    {order.approvedAt && (
                      <span>{t('changeOrders.status.APPROVED')}: <strong className="text-[#1C1A17]">{formatDate(order.approvedAt)}</strong></span>
                    )}
                  </div>

                  {/* D-25AGO-09 — the APPROVE / REJECT decision. Offered ONLY from
                      PENDING, and only for a real (non-demo) project. APPROVED and
                      REJECTED are terminal, so a decided order shows no verb at
                      all: `isValidChangeOrderTransition` is the SAME function the
                      server guards with, so the button set can never offer an edge
                      the server would refuse. */}
                  {!isDemo && isValidChangeOrderTransition(order.status as ChangeOrderStatus, 'APPROVED') && (
                    <div data-testid={`co-decision-${order.id}`} className="pt-1">
                      {!isDecisionTarget && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            data-testid={`co-approve-btn-${order.id}`}
                            onClick={() => openDecision(order.id, 'APPROVED')}
                            className="w-full sm:w-auto min-h-[44px] rounded-full border border-[#B7E4C7] bg-[#ECFDF5] px-4 text-[12px] font-[800] text-[#15803D]"
                          >
                            {isEs ? CO_DECISION_COPY.approveBtn.es : CO_DECISION_COPY.approveBtn.en}
                          </button>
                          <button
                            type="button"
                            data-testid={`co-reject-btn-${order.id}`}
                            onClick={() => openDecision(order.id, 'REJECTED')}
                            className="w-full sm:w-auto min-h-[44px] rounded-full border border-[#E7C3C5] bg-white px-4 text-[12px] font-[800] text-[#B4232A]"
                          >
                            {isEs ? CO_DECISION_COPY.rejectBtn.es : CO_DECISION_COPY.rejectBtn.en}
                          </button>
                        </div>
                      )}

                      {isDecisionTarget && (
                        <div
                          data-testid={`co-decision-confirm-${order.id}`}
                          className="mt-1 space-y-1.5 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2.5"
                        >
                          <p className="text-[11px] font-[900] text-[#1C1A17]">
                            {isApproving
                              ? (isEs ? CO_DECISION_COPY.approveTitle.es : CO_DECISION_COPY.approveTitle.en)
                              : (isEs ? CO_DECISION_COPY.rejectTitle.es : CO_DECISION_COPY.rejectTitle.en)}
                          </p>
                          <p className="text-[10px] font-[600] text-[#6B655C] leading-relaxed">
                            {isApproving
                              ? (isEs ? CO_DECISION_COPY.approveBody.es : CO_DECISION_COPY.approveBody.en)
                              : (isEs ? CO_DECISION_COPY.rejectBody.es : CO_DECISION_COPY.rejectBody.en)}
                          </p>

                          {/* Spec §2.3 heading 3: the approve dialog MUST state the
                              amount and the resulting total. A one-click approve
                              with no amount shown is PROHIBITED. */}
                          {isApproving && (
                            <div data-testid={`co-approve-figures-${order.id}`} className="space-y-0.5">
                              <div className="flex items-baseline justify-between gap-2 text-[11px] font-[700] text-[#1C1A17]">
                                <span>{isEs ? CO_DECISION_COPY.amountLabel.es : CO_DECISION_COPY.amountLabel.en}</span>
                                <span className="font-[900]">{formatMoneyValue(order.amount, isEs)}</span>
                              </div>
                              <div className="flex items-baseline justify-between gap-2 text-[10px] font-[600] text-[#6B655C]">
                                <span>{isEs ? CO_DECISION_COPY.resultingTotalLabel.es : CO_DECISION_COPY.resultingTotalLabel.en}</span>
                                <span className="font-[800] text-[#1C1A17]">{formatMoneyValue(approvedTotal + order.amount, isEs)}</span>
                              </div>
                              <p className="text-[9px] font-[600] text-[#9E6B00] pt-0.5">
                                {isEs ? CO_DECISION_COPY.downstreamNote.es : CO_DECISION_COPY.downstreamNote.en}
                              </p>
                            </div>
                          )}

                          {!isApproving && (
                            <>
                              <label
                                htmlFor={`co-reject-reason-${order.id}`}
                                className="block text-[9px] font-[800] uppercase tracking-wide text-[#9E9890]"
                              >
                                {isEs ? CO_DECISION_COPY.reasonLabel.es : CO_DECISION_COPY.reasonLabel.en}
                              </label>
                              <textarea
                                id={`co-reject-reason-${order.id}`}
                                data-testid={`co-reject-reason-${order.id}`}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={2}
                                placeholder={isEs ? CO_DECISION_COPY.reasonPlaceholder.es : CO_DECISION_COPY.reasonPlaceholder.en}
                                className="w-full rounded-md border border-[#ECEAE4] px-2 py-1.5 text-[11px] font-[600] text-[#1C1A17]"
                              />
                            </>
                          )}

                          {decisionError && (
                            <p data-testid={`co-decision-error-${order.id}`} className="text-[10px] font-[800] text-[#B4232A]">
                              {decisionError}
                            </p>
                          )}

                          <div className="flex flex-col sm:flex-row gap-2 pt-0.5">
                            <button
                              type="button"
                              data-testid={`co-decision-confirm-btn-${order.id}`}
                              disabled={deciding || (!isApproving && rejectReason.trim().length === 0)}
                              onClick={() => runDecision(order, decision!.next)}
                              className={`w-full min-h-[44px] rounded-full px-3 text-[11px] font-[800] text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                                isApproving ? 'bg-[#15803D]' : 'bg-[#B4232A]'
                              }`}
                            >
                              {deciding
                                ? (isEs ? CO_DECISION_COPY.workingBtn.es : CO_DECISION_COPY.workingBtn.en)
                                : isApproving
                                  ? (isEs ? CO_DECISION_COPY.confirmApprove.es : CO_DECISION_COPY.confirmApprove.en)
                                  : (isEs ? CO_DECISION_COPY.confirmReject.es : CO_DECISION_COPY.confirmReject.en)}
                            </button>
                            <button
                              type="button"
                              data-testid={`co-decision-cancel-btn-${order.id}`}
                              disabled={deciding}
                              onClick={closeDecision}
                              className="w-full min-h-[44px] rounded-full border border-[#D6CFC4] bg-white px-3 text-[11px] font-[800] text-[#6B655C]"
                            >
                              {isEs ? CO_DECISION_COPY.keepCta.es : CO_DECISION_COPY.keepCta.en}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── ADD FORM ─────────────────────────────────────

function AddChangeOrderForm({ projectId, onClose, onAdded }: {
  projectId: string; onClose: () => void; onAdded: () => void
}) {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const [form, setForm] = useState({ title: '', description: '', reason: 'CLIENT', amount: '', phase: '', impactDays: '0', requestedBy: '' })

  const mutation = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/change-orders`, {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
      // `status` is deliberately NOT sent: the server owns the initial state
      // (DB default PENDING) and zod strips any status key, so sending 'DRAFT'
      // only made the request lie about what it created.
      body: JSON.stringify({ ...form, amount: Number(form.amount), impactDays: Number(form.impactDays) || 0 })
    }).then(async r => {
      // PR3 (C3): a rejected save used to close the form as if it worked.
      if (!r.ok) throw new Error(`change_order_create_failed_${r.status}`)
      return r.json()
    }) },
    onSuccess: onAdded
  })
  // PR3 (C3): every field the server requires must be filled before Save.
  const missing = !form.title.trim() || !form.description.trim() || !form.phase.trim() || !form.requestedBy.trim() || form.amount === ''

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800]">{t('changeOrders.form.newOrder')}</h3>
      <input placeholder={`${t('changeOrders.form.title')} *`} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      <textarea placeholder={`${t('changeOrders.form.description')} *`} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B] h-[60px] resize-none" />
      <div className="grid grid-cols-2 gap-2">
        <select value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]">
          {REASON_KEYS.map((key) => (
            <option key={key} value={key}>{t(`changeOrders.reason.${key}`)}</option>
          ))}
        </select>
        <input placeholder={`${t('changeOrders.form.phase')} *`} value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input placeholder={`${t('changeOrders.form.amount')} *`} type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder={t('changeOrders.form.impactDays')} type="number" value={form.impactDays} onChange={e => setForm({ ...form, impactDays: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder={`${t('changeOrders.form.requestedBy')} *`} value={form.requestedBy} onChange={e => setForm({ ...form, requestedBy: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button onClick={() => mutation.mutate()} disabled={missing || mutation.isPending}
          className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40">
          {mutation.isPending ? '...' : t('Save')}
        </button>
      </div>
      <p className="text-[10px] font-[600] text-[#9E9890]">
        {isEs ? '* Todos los campos son obligatorios, salvo los días de impacto.' : '* All fields are required except impact days.'}
      </p>
      {mutation.isError && (
        <p role="alert" data-testid="co-create-error" className="text-[11px] font-[700] text-red-700">
          {isEs ? 'No se pudo guardar la orden de cambio. Revisa los datos e intenta de nuevo.' : 'Could not save the change order. Check the fields and try again.'}
        </p>
      )}
    </div>
  )
}
